import { useCallback } from "react";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { SupabaseTagsService } from "../../tags/server/supabase-tags-service";
import { SupabaseDigitalAssetService, toAsset } from "../server/digital-asset-service";
import { SupabaseDigitalAssetTagService } from "../server/digital-asset-tag-service";

import type { UUID } from "../../../utils/uuid";
import type { Asset, AssetUpsert } from "../server/digital-asset-service";

export type AssetResult =
  | { success: true; asset: Asset; error?: undefined }
  | {
      success: false;
      error: { key: "name" | null; message: string } | undefined;
      asset?: undefined;
    };
type SaveTagsResult =
  | { success: true; tags: string[]; error?: undefined }
  | { success: false; error: { key: null; message: string } | undefined; tags?: undefined };

const normalizeTags = (tags?: string[]): string[] => {
  const normalizedTags: string[] = [];
  const tagNames = new Set<string>();
  for (const tag of tags ?? []) {
    const trimmedTag = tag.trim();
    if (!trimmedTag || tagNames.has(trimmedTag)) continue;
    tagNames.add(trimmedTag);
    normalizedTags.push(trimmedTag);
  }
  return normalizedTags;
};

export const useUpsertAssetCallback = (): ((
  update: AssetUpsert,
) => Promise<AssetResult | undefined>) =>
  useSupabaseCallback(async ({ supabase }, asset: AssetUpsert) => {
    const result = await new SupabaseDigitalAssetService(supabase).upsert(asset);
    if (!result || result?.error) {
      return {
        success: false,
        error: result?.error.message.includes("idx_unique_title")
          ? { key: "name", message: "Asset title must be unique" }
          : undefined,
      };
    }

    return { success: true, asset: toAsset(result.data) };
  }, []);

export const useCreateAssetCallback = (): (({
  title,
  thumbnailUrl,
  contentType,
  projectId,
  pageId,
  mimeType,
  sourceDigitalAssetId,
  publicUrl,
  customTemplate,
  externalStorageId,
  tags,
  pageCount,
  folderId,
}: {
  title: string;
  thumbnailUrl?: string | null;
  contentType: string;
  projectId?: string | null;
  pageId?: string | null;
  mimeType?: string | null;
  sourceDigitalAssetId?: UUID | null;
  publicUrl?: string | null;
  customTemplate?: boolean;
  externalStorageId?: string | null;
  tags?: string[];
  pageCount?: number | null;
  folderId?: UUID | null;
}) => Promise<AssetResult>) => {
  const upsert = useUpsertAssetCallback();
  const saveAssetTags = useSupabaseCallback(
    async ({ supabase }, assetId: UUID, tags: string[]): Promise<SaveTagsResult> => {
      const normalizedTags = normalizeTags(tags);
      const tagService = new SupabaseTagsService(supabase);
      const tagIds: UUID[] = [];
      for (const tagName of normalizedTags) {
        // oxlint-disable-next-line no-await-in-loop -- sequential DB writes
        const { data, error } = await tagService.upsert(
          { name: tagName },
          { onConflict: "account_id,name" },
        );
        if (error || !data) {
          return {
            success: false,
            error: { key: null, message: error?.message ?? "Failed to save tags" },
          };
        }
        tagIds.push(data.id);
      }

      const { error } = await new SupabaseDigitalAssetTagService(supabase).setAssetTags(
        assetId,
        tagIds,
      );
      if (error) {
        return {
          success: false,
          error: { key: null, message: error.message ?? "Failed to save tags" },
        };
      }

      return {
        success: true,
        tags: normalizedTags,
      };
    },
    [],
  );
  return useCallback(
    async ({
      title,
      thumbnailUrl,
      contentType,
      projectId,
      pageId,
      mimeType,
      sourceDigitalAssetId,
      publicUrl,
      customTemplate,
      externalStorageId,
      tags,
      pageCount,
      folderId,
    }) => {
      const result = await upsert({
        project_id: projectId,
        title,
        content_type: contentType,
        page_id: pageId,
        thumbnail_url: thumbnailUrl,
        mime_type: mimeType,
        source_digital_asset_id: sourceDigitalAssetId,
        public_url: publicUrl,
        custom_source_template: customTemplate ?? false,
        external_storage_id: externalStorageId,
        tags,
        page_count: pageCount,
        folder_id: folderId,
      });

      if (!result || !result.success || tags === undefined) {
        return result ?? { success: false, error: undefined };
      }

      const tagsResult = await saveAssetTags(result.asset.id, tags);
      if (!tagsResult || !tagsResult.success) {
        return tagsResult ?? { success: false, error: undefined };
      }

      return { success: true, asset: { ...result.asset, tags: tagsResult.tags } };
    },
    [saveAssetTags, upsert],
  );
};

export const useUpdateAssetCallback = (): (({
  asset,
  title,
  thumbnailUrl,
  projectId,
  publicUrl,
  externalStorageId,
}: {
  asset: Asset;
  title?: string;
  thumbnailUrl?: string;
  projectId?: string | null;
  publicUrl?: string | null;
  externalStorageId?: string | null;
}) => Promise<AssetResult>) => {
  const upsert = useUpsertAssetCallback();
  return useCallback(
    async ({ asset, title, thumbnailUrl, projectId, publicUrl, externalStorageId }) =>
      (await upsert({
        ...asset,
        title: title ?? asset.title,
        thumbnail_url: thumbnailUrl ?? asset.thumbnail_url,
        project_id: projectId ?? asset.project_id,
        page_id: asset.page_id,
        public_url: publicUrl ?? asset.public_url,
        external_storage_id: externalStorageId,
      })) ?? { success: false, error: undefined },
    [upsert],
  );
};
